import React, { useEffect, useState } from "react";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import { useParams } from "react-router-dom";

import { Button, Card, Image } from "@nextui-org/react";
import MarkdownWithInputs from "../../components/Policies/MarkdownwithInputs";
import {
  UpdateOneData,
  capitalize,
  getDataWithFilterUrl,
  separateDasFromUrl,
} from "../../controllers/APIController";
import { editIcon } from "../../utils/constant";
import TurndownService from "turndown";
import useGetFrequencyCounter from "../../hooks/useGetFrequencyCounter";
import LoadingPage from "../../components/Loader/LoadingPage";
import SingleControlMetricCard from "../../components/Policies/SingleControlMetricCard";
import { getImageForDomain } from "../../constant/Policies/policyConstant";
import { StandardDeleteButton } from "../../components/StandardButtons";

/**
 * Extract procedure fields from markdown text using handlebar syntax
 * Finds all {{fieldName}} patterns and creates an object with empty values
 * @param {string} text - Markdown text containing handlebar placeholders
 * @returns {Object} Object with field names as keys and empty strings as values
 */
const getAllProcedureFields = (text) => {
  let data = {};
  const handlebarRegex = /{{([^}]+)}}/g;
  let match;
  
  while ((match = handlebarRegex.exec(text)) !== null) {
    const fieldName = match[1].trim();
    data = { ...data, [fieldName]: "" };
  }
  
  return data;
};

/**
 * Procedures Component
 * Displays and manages a single procedure with its control metrics
 * Allows editing procedure content and viewing associated control metrics
 * @returns {JSX.Element} Single procedure detail view
 */
const Procedures = () => {
  // Custom hooks for frequency counter data
  const { FrequencyCounterList } = useGetFrequencyCounter();
  const turndownService = new TurndownService();

  // URL parameters for routing
  const { _procedure, _orgId: currentOrgId, _policy } = useParams();
  const procedureName = _procedure ? _procedure.replace(/-/g, " ") : "";

  // User and organization information from session storage
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const orgInfo = userLogged && userLogged.user.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + orgInfo;

  // Procedure title and filter URL
  const headingTitle = capitalize(separateDasFromUrl(procedureName));
  const procedureFilteredUrl =
    orgFilterUrl + "&filters[Name][$eqi]=" + headingTitle;

  // Component state
  const [isLoading, setIsLoading] = useState(true);
  const [procedureData, setProcedureData] = useState({});
  const [controlMetricList, setControlMetricList] = useState([]);
  const [procedure, setProcedure] = useState(null);
  const [errorMessage, setErrorMessage] = useState({});

  /**
   * Validate that all required fields are filled
   * Checks if any procedure fields are empty and sets error messages
   * @returns {boolean} True if validation fails, false if passes
   */
  const validateForm = () => {
    let hasErrors = false;
    const errors = {};
    
    for (let key in procedureData) {
      if (procedureData[key] === "") {
        hasErrors = true;
        errors[key] = "This field is required!";
      }
    }
    
    if (hasErrors) setErrorMessage(errors);
    return hasErrors;
  };

  /**
   * Handle form submission to update procedure
   * Validates form data and updates the procedure in the backend
   */
  const submitHandler = async () => {
    if (validateForm()) return;

    const updatedProcedureData = {
      Response: { ...procedureData },
    };

    await UpdateOneData(
      "procedures",
      procedure?.id,
      updatedProcedureData
    );

    window.location.reload();
  };

  /**
   * Reset procedure data to original values
   * Fetches the original procedure and resets all changes
   */
  const resetProcedureData = async () => {
    const procedureOriginId = procedure?.attributes?.OriginId;
    const filteredUrl = `filters[id][$eqi]=${procedureOriginId}`;
    const response = await getDataWithFilterUrl("procedures", filteredUrl);
    const originProcedureInfo = response?.data[0]?.attributes;

    const data = {
      ProcedureFramework: originProcedureInfo?.ProcedureFramework,
      Response: null
    };
    await UpdateOneData("procedures", procedure?.id, data);

    window.location.reload();
  };

  /**
   * Fetch procedure data and control metrics on component mount
   * Loads procedure details and extracts control metric information
   */
  useEffect(() => {
    getDataWithFilterUrl("procedures", procedureFilteredUrl)
      .then((data) => {
        // Check if data.data exists and has length > 0 before setting procedure
        if (data.data && data.data.length > 0) {
          setProcedure(data.data[0]);

          // Set procedure response data or extract from framework
          const procedureResponse =
            data.data[0]?.attributes?.Response ||
            getAllProcedureFields(data.data[0]?.attributes?.ProcedureFramework);

          setProcedureData(procedureResponse);

          // Extract control metrics list
          setControlMetricList(
            data?.data[0]?.attributes?.ControlMetrics?.controlMetrics
          );
          setIsLoading(false);
        } else {
          setProcedure(null); // Set to null if no procedure data found
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching procedure data:", error);
        setProcedure(null); // Handle error by setting procedure to null
        setIsLoading(false);
      });
  }, [procedureFilteredUrl]);

  // Convert HTML to markdown for display
  const markdownData = turndownService.turndown(
    procedure?.attributes?.ProcedureFramework || ""
  );

  return (
    <BreadCrumbsPage>
      <LoadingPage isLoading={isLoading}>
        <div>
          {/* Procedure Header */}
          {procedure && (
            <div className="flex justify-between">
              <p className="text-3xl font-medium flex gap-4 items-center">
                <div>
                  <Image
                    alt="Procedure domain logo"
                    height={40}
                    radius="sm"
                    src={getImageForDomain(procedure.attributes.DomainName)}
                    width={40}
                  />
                </div>
                {headingTitle}
              </p>
              <StandardDeleteButton onClick={submitHandler} aria-label="Update procedure">
                Update Procedure
              </StandardDeleteButton>
            </div>
          )}

          {/* Procedure Description */}
          {procedure && (
            <div>
              <div className="my-8">{procedure.attributes.Description}</div>
            </div>
          )}
          
          {/* Main Content Layout */}
          <div className="flex items-start justify-between gap-4 mt-8">
            {/* Procedure Content Section */}
            <div className="flex justify-between w-[65%]">
              {procedure && (
                <Card
                  className="w-full p-4 border border-divider"
                  shadow="none"
                >
                  <div>
                    {/* Action Buttons */}
                    <div className="flex justify-end gap-2">
                      <Button
                        color="danger"
                        variant="ghost"
                        onClick={() => {}} // TODO: Implement edit functionality
                        className="flex items-center justify-center"
                        aria-label="Edit procedure"
                      >
                        {editIcon}
                        <span>Edit</span>
                      </Button>
                      <Button
                        color="secondary"
                        variant="ghost"
                        onClick={resetProcedureData}
                        className="flex items-center justify-center"
                        aria-label="Reset procedure data"
                      >
                        <span>Reset</span>
                      </Button>
                    </div>
                    
                    {/* Procedure Framework Section */}
                    <p className="text-xl font-medium mt-8">
                      Procedure Framework
                    </p>
                    <div className="mt-4">
                      <MarkdownWithInputs
                        markdownContent={
                          procedure.attributes.ProcedureFramework
                        }
                        procedureData={procedureData}
                        onChangeProcedureData={setProcedureData}
                        errorMessage={errorMessage}
                      />
                    </div>
                  </div>
                </Card>
              )}
            </div>

            {/* Control Metrics Sidebar */}
            {controlMetricList.length > 0 && (
              <div className="flex flex-col gap-4 w-[30%] max-h-[500px] overflow-auto hide-scrollbar">
                <p className="font-medium text-xl text-center">
                  Control Metrics
                </p>
                {controlMetricList?.map((item) => (
                  <SingleControlMetricCard
                    key={item}
                    id={+item}
                    FrequencyCounterList={FrequencyCounterList}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </LoadingPage>
    </BreadCrumbsPage>
  );
};

export default Procedures;
